import { Field, Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Input from "../../components/form/Input";
import Spinner from "../../components/misc/Spinner";
import { DEV_MODE } from "../../constants";
import { Role, useAuthStore } from "../../stores/auth";
import { useDeliveryNoteTemplatesStore } from "../../stores/delivery_note_templates";
import { useItemStore } from "../../stores/items";
import { useToastStore } from "../../stores/toast";

const DOC_COL_TYPES = ["text", "checkbox", "select"];

const createInitialValues = (template) => ({
  name: template?.name || "",
  items:
    template?.items?.map((item) => ({
      number: item.number,
      name: item.name,
      id: item.id,
      groups: item.groups || [],
    })) || [],
  documentation: template?.documentation || [],
});

const groupItems = (items) => {
  const groupMap = new Map();
  const ungrouped = [];

  items.forEach((item, flatIndex) => {
    const group = item.groups?.[0];
    if (group) {
      const key = group.id;
      if (!groupMap.has(key)) {
        groupMap.set(key, { id: key, name: group.name, items: [] });
      }
      groupMap.get(key).items.push({ ...item, flatIndex });
    } else {
      ungrouped.push({ ...item, flatIndex });
    }
  });

  const groups = [...groupMap.values()];
  if (ungrouped.length > 0) {
    groups.push({ id: null, name: null, items: ungrouped });
  }
  return groups;
};

const createValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().required(t("form.errors.required", { attr: t("delivery_notes.templates.name") })),
  });

function ItemSearchInput({ onAdd, existingNumbers }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [fetchSome, byIds, allIds] = useItemStore((state) => [state.fetchSome, state.byIds, state.allIds], shallow);

  const doSearch = useCallback(async (q) => {
    await fetchSome({ q, limit: 20 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (query.length >= 1) {
      doSearch(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Derived from store state — no Effect needed
  const results = query.length >= 1 ? allIds.map((id) => byIds[id]) : [];

  const filteredResults = results.filter((item) => !existingNumbers.includes(item.number));

  return (
    <div className="position-relative">
      <div className="input-group">
        <div className="input-group-text bg-light">
          <i className="fa fa-search opacity-5"></i>
        </div>
        <input
          type="text"
          className="form-control bg-light"
          placeholder={t("delivery_notes.templates.search_item") + "..."}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {query.length >= 1 && filteredResults.length > 0 && (
        <div
          className="position-absolute w-100 bg-white border shadow-sm"
          style={{ zIndex: 10, maxHeight: "200px", overflowY: "auto" }}
        >
          {filteredResults.map((item) => (
            <button
              key={item.id}
              type="button"
              className="dropdown-item py-2 px-3"
              onClick={() => {
                onAdd(item);
                setQuery("");
              }}
            >
              <strong>{item.number}</strong> - {item.name}
              {item.groups?.[0] && <span className="text-muted ms-2">({item.groups[0].name})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemsCard({ items, setFieldValue }) {
  const { t } = useTranslation();

  const moveItem = (flatIndex, direction) => {
    const currentGroups = groupItems(items);

    for (const group of currentGroups) {
      const posIdx = group.items.findIndex((i) => i.flatIndex === flatIndex);
      if (posIdx === -1) continue;

      const targetPosIdx = posIdx + direction;
      if (targetPosIdx < 0 || targetPosIdx >= group.items.length) return;

      [group.items[posIdx], group.items[targetPosIdx]] = [group.items[targetPosIdx], group.items[posIdx]];

      const newItems = currentGroups.flatMap((g) => g.items.map(({ flatIndex: _, ...item }) => item));
      setFieldValue("items", newItems);
      return;
    }
  };

  const moveGroup = (groupIdx, direction) => {
    const currentGroups = groupItems(items);
    const targetIdx = groupIdx + direction;
    if (targetIdx < 0 || targetIdx >= currentGroups.length) return;

    const newGroups = [...currentGroups];
    [newGroups[groupIdx], newGroups[targetIdx]] = [newGroups[targetIdx], newGroups[groupIdx]];

    const newItems = newGroups.flatMap((g) => g.items.map(({ flatIndex, ...item }) => item));
    setFieldValue("items", newItems);
  };

  const removeItem = (index) => {
    setFieldValue(
      "items",
      items.filter((_, i) => i !== index),
    );
  };

  const addItem = (item) => {
    const newItem = {
      number: item.number,
      name: item.name,
      id: item.id,
      groups: item.groups || [],
    };
    const group = newItem.groups?.[0];

    if (group) {
      // Insert after the last item of the same group
      const lastGroupIndex = items.reduce((last, curr, i) => (curr.groups?.[0]?.id === group.id ? i : last), -1);
      if (lastGroupIndex >= 0) {
        const newItems = [...items];
        newItems.splice(lastGroupIndex + 1, 0, newItem);
        setFieldValue("items", newItems);
        return;
      }
    }

    setFieldValue("items", [...items, newItem]);
  };

  const existingNumbers = items.map((i) => i.number);
  const groups = groupItems(items);

  return (
    <div className="card border-0 mb-4">
      <div className="card-header h6 mb-0 bg-none p-3">
        <i className="fa fa-gear fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("delivery_notes.templates.items")}
      </div>
      <div className="card-body">
        <div className="table-responsive mb-3">
          <table className="table table-hover table-panel align-middle mb-0">
            <thead>
              <tr>
                <th width={1}>{t("common.position")}</th>
                <th width={1}>{t("common.number_short")}</th>
                <th>{t("items.name")}</th>
                <th width={1}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, groupIdx) => (
                <React.Fragment key={group.id ?? "ungrouped"}>
                  <tr>
                    <td colSpan={3} className="fw-bold bg-light text-muted py-2 ps-3">
                      <i className="fa fa-folder-open fa-fw me-1"></i>
                      {group.name || t("items.groups.ungrouped")}
                    </td>
                    <td className="bg-light text-nowrap text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-default me-1"
                        disabled={groupIdx === 0}
                        onClick={() => moveGroup(groupIdx, -1)}
                      >
                        <i className="fa fa-arrow-up" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-default"
                        disabled={groupIdx === groups.length - 1}
                        onClick={() => moveGroup(groupIdx, 1)}
                      >
                        <i className="fa fa-arrow-down" />
                      </button>
                    </td>
                  </tr>
                  {group.items.map((item, posIndex) => {
                    const isFirst = posIndex === 0;
                    const isLast = posIndex === group.items.length - 1;
                    return (
                      <tr key={`${item.number}-${item.flatIndex}`}>
                        <td className="text-center">{posIndex + 1}</td>
                        <td>{item.number}</td>
                        <td>{item.name}</td>
                        <td className="text-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-default me-1"
                            disabled={isFirst}
                            onClick={() => moveItem(item.flatIndex, -1)}
                          >
                            <i className="fa fa-arrow-up" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-default me-1"
                            disabled={isLast}
                            onClick={() => moveItem(item.flatIndex, 1)}
                          >
                            <i className="fa fa-arrow-down" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removeItem(item.flatIndex)}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">
                    {t("delivery_notes.templates.no_items")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <ItemSearchInput onAdd={addItem} existingNumbers={existingNumbers} />
      </div>
    </div>
  );
}

function DocumentationCard({ documentation, setFieldValue }) {
  const { t } = useTranslation();

  const addColumn = () => {
    const newCol = { name: "", type: "text", value: [], choices: [] };
    if (documentation.length === 0) {
      setFieldValue("documentation", [[newCol]]);
    } else {
      const newDoc = documentation.map((row) => [...row, newCol]);
      setFieldValue("documentation", newDoc);
    }
  };

  const removeColumn = (colIndex) => {
    const newDoc = documentation.map((row) => row.filter((_, ci) => ci !== colIndex)).filter((row) => row.length > 0);
    setFieldValue("documentation", newDoc);
  };

  const addRow = () => {
    if (documentation.length === 0) return;
    const colCount = documentation[0].length;
    const newRow = Array.from({ length: colCount }, () => ({
      name: "",
      type: "text",
      value: [],
      choices: [],
    }));
    setFieldValue("documentation", [...documentation, newRow]);
  };

  const removeRow = (rowIndex) => {
    setFieldValue(
      "documentation",
      documentation.filter((_, i) => i !== rowIndex),
    );
  };

  const updateCol = (rowIndex, colIndex, field, value) => {
    const newDoc = documentation.map((row, ri) =>
      row.map((col, ci) => {
        if (ri === rowIndex && ci === colIndex) {
          return { ...col, [field]: value };
        }
        return col;
      }),
    );
    setFieldValue("documentation", newDoc);
  };

  const columns = documentation.length > 0 ? documentation[0] : [];

  return (
    <div className="card border-0 mb-4">
      <div className="card-header h6 mb-0 bg-none p-3 d-flex align-items-center">
        <div>
          <i className="fa fa-table fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("menu.documentation")}
        </div>
        <div className="ms-auto">
          <button type="button" className="btn btn-sm btn-default me-1" onClick={addColumn}>
            <i className="fa fa-plus me-1" />
            {t("delivery_notes.templates.add_column")}
          </button>
          {documentation.length > 0 && (
            <button type="button" className="btn btn-sm btn-default" onClick={addRow}>
              <i className="fa fa-plus me-1" />
              {t("delivery_notes.templates.add_row")}
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {columns.length === 0 ? (
          <div className="text-center text-muted py-3">{t("delivery_notes.templates.no_documentation")}</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead>
                <tr>
                  {columns.map((col, colIndex) => (
                    <th key={colIndex} style={{ minWidth: "180px" }}>
                      <div className="d-flex align-items-center">
                        <input
                          type="text"
                          className="form-control form-control-sm me-1"
                          value={documentation[0][colIndex].name}
                          placeholder={t("delivery_notes.templates.col_name")}
                          onChange={(e) =>
                            updateColHeader(colIndex, "name", e.target.value, documentation, setFieldValue)
                          }
                        />
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeColumn(colIndex)}>
                          <i className="fa fa-times" />
                        </button>
                      </div>
                      <select
                        className="form-select form-select-sm mt-1"
                        value={documentation[0][colIndex].type}
                        onChange={(e) =>
                          updateColHeader(colIndex, "type", e.target.value, documentation, setFieldValue)
                        }
                      >
                        {DOC_COL_TYPES.map((dtype) => (
                          <option key={dtype} value={dtype}>
                            {t(`delivery_notes.templates.types.${dtype}`)}
                          </option>
                        ))}
                      </select>
                      {documentation[0][colIndex].type === "select" && (
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder={t("delivery_notes.templates.choices_hint")}
                          value={(documentation[0][colIndex].choices || []).join(", ")}
                          onChange={(e) =>
                            updateColHeader(
                              colIndex,
                              "choices",
                              e.target.value.split(",").map((s) => s.trim()),
                              documentation,
                              setFieldValue,
                            )
                          }
                        />
                      )}
                    </th>
                  ))}
                  <th width={1}></th>
                </tr>
              </thead>
              <tbody>
                {documentation.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((col, colIndex) => (
                      <td key={colIndex}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={(col.value || []).join(", ")}
                          placeholder={t("delivery_notes.templates.default_value")}
                          onChange={(e) =>
                            updateCol(rowIndex, colIndex, "value", e.target.value ? [e.target.value] : [])
                          }
                        />
                      </td>
                    ))}
                    <td>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => removeRow(rowIndex)}>
                        <i className="fa fa-times" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function updateColHeader(colIndex, field, value, documentation, setFieldValue) {
  const newDoc = documentation.map((row) =>
    row.map((col, ci) => {
      if (ci === colIndex) {
        return { ...col, [field]: value };
      }
      return col;
    }),
  );
  setFieldValue("documentation", newDoc);
}

function DeliveryNoteTemplateAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const templateId = Number.parseInt(params.templateId);
  const isAddMode = !templateId;
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  const [hasRole] = useAuthStore((state) => [state.hasRole], shallow);
  const canEdit = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);

  const [byIds, getById, createOne, updateOne] = useDeliveryNoteTemplatesStore(
    (state) => [state.byIds, state.getById, state.createOne, state.updateOne],
    shallow,
  );

  const template = Number.isInteger(templateId) ? byIds[templateId] : null;

  useEffect(() => {
    if (templateId) {
      getById({ id: templateId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const handleSubmit = async (values, { setErrors }) => {
    const payload = {
      name: values.name,
      item_ids: values.items.map((i) => i.id),
      documentation: values.documentation,
    };

    const { error } = isAddMode ? await createOne(payload) : await updateOne({ id: templateId, ...payload });

    if (error) {
      const msg = error?.detail?.[0]?.t || error?.detail;
      addToast("error", msg ? t(msg) : JSON.stringify(error));
    } else {
      addToast("success", t("common.saved"));
    }
  };

  if (Number.isInteger(templateId) && !template) {
    return <Spinner />;
  }

  if (!canEdit) {
    navigate("/");
    return null;
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("delivery_notes.templates.templates"), to: "/delivery_note_templates" },
          { label: isAddMode ? t("menu.add") : template.name },
        ]}
      />

      <Formik
        initialValues={createInitialValues(template)}
        validationSchema={createValidationSchema(t)}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={false}
        validateOnMount={false}
        onSubmit={handleSubmit}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <div className="row">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-copy fa-lg fa-fw text-dark text-opacity-50 me-1"></i>{" "}
                    {t("delivery_notes.templates.template")}
                  </div>
                  <div className="card-body">
                    <div className="row mb-3 gx-3">
                      <div className="col-md">
                        <label className="form-label" htmlFor="name">
                          {t("delivery_notes.templates.name")}
                        </label>
                        <Field component={Input} name="name" className="form-control fs-13px h-45px" />
                      </div>
                    </div>
                  </div>
                </div>

                <ItemsCard items={values.items} setFieldValue={setFieldValue} />

                <DocumentationCard documentation={values.documentation} setFieldValue={setFieldValue} />

                <div className="card border-0 mb-4">
                  <div className="card-body">
                    <div className="btn btn-group d-flex">
                      <button className="btn btn-success w-100" disabled={isSubmitting}>
                        {t("menu.submit")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {DEV_MODE && (
              <pre
                className="m-t-xl"
                style={{
                  fontSize: "1rem",
                  padding: ".25rem .5rem",
                  overflowX: "scroll",
                }}
              >
                VALUES: {JSON.stringify(values, null, 2)}
                <br />
                ERRORS: {JSON.stringify(errors, null, 2)}
              </pre>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default DeliveryNoteTemplateAddEdit;
